import React, { useState, useEffect } from "react";
import { Plus, Check, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import MobileSelect from "@/components/drill/MobileSelect";

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const HEADINGS = ["Bits", "Casing", "Consumables", "Muds", "Oils", "PPE", "Rig spares", "subs"].map(capitalize).sort((a,b)=>a.localeCompare(b));
const UOM_OPTIONS = ["bag", "box", "drum", "each", "kg", "L", "pallet"].map(v=>({value:v,label:v}));
const OPTIONS = HEADINGS.map((h) => ({ value: h, label: h }));
const SUB_OPTIONS = [
  { value: "box", label: "box" },
  { value: "pin", label: "pin" },
];

export default function StocktakeAddSplash({
  open,
  onOpenChange,
  onAdd,
  initial,
  onEdit,
  onDelete,
}) {
  const editing = Boolean(initial);
  const [heading, setHeading] = useState("");
  const [itemName, setItemName] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [sub1Sel, setSub1Sel] = useState("");
  const [sub1Desc, setSub1Desc] = useState("");
  const [sub2Sel, setSub2Sel] = useState("");
  const [sub2Desc, setSub2Desc] = useState("");
  const [bitThread, setBitThread] = useState("");
  const [bitSize, setBitSize] = useState("");
  const [bitType, setBitType] = useState("");
  const [casingLength, setCasingLength] = useState("");
  const [casingType, setCasingType] = useState("");
  const [casingSize, setCasingSize] = useState("");
  const [buildTo, setBuildTo] = useState("");
  const [uom, setUom] = useState("");

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setHeading(initial.heading || "");
      setItemName(initial.name || "");
      setQuantity(initial.quantity ?? 1);
      const s = initial.subs || [];
      setSub1Sel(s[0]?.selection || "");
      setSub1Desc(s[0]?.description || "");
      setSub2Sel(s[1]?.selection || "");
      setSub2Desc(s[1]?.description || "");
      const b = initial.bits || {};
      setBitThread(b.thread || "");
      setBitSize(b.size || "");
      setBitType(b.type || "");
      const c = initial.casing || {};
      setCasingLength(c.length || "");
      setCasingType(c.type || "");
      setCasingSize(c.size || "");
      setBuildTo(initial.build_to != null ? String(initial.build_to) : "");
      setUom(initial.uom || "");
    } else {
      setHeading("");
      setItemName("");
      setQuantity(1);
      setSub1Sel("");
      setSub1Desc("");
      setSub2Sel("");
      setSub2Desc("");
      setBitThread("");
      setBitSize("");
      setBitType("");
      setCasingLength("");
      setCasingType("");
      setCasingSize("");
      setBuildTo("");
      setUom("");
    }
  }, [open, initial]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const subsParts = [];
    if (sub1Desc.trim()) subsParts.push(sub1Desc.trim());
    if (sub1Sel) subsParts.push(`[${sub1Sel}]`);
    if (sub2Desc.trim()) subsParts.push(sub2Desc.trim());
    if (sub2Sel) subsParts.push(`[${sub2Sel}]`);
    const computedName =
      heading === "Subs"
        ? subsParts.join(" ").trim() || "Sub"
        : itemName.trim();
    if (!heading || !computedName) return;
    let subs = null;
    let bits = null;
    let casing = null;
    if (heading === "Subs") {
      subs = [
        { selection: sub1Sel, description: sub1Desc.trim() },
        { selection: sub2Sel, description: sub2Desc.trim() },
      ];
    }
    if (heading === "Bits") {
      bits = {
        thread: bitThread.trim(),
        size: bitSize.trim(),
        type: bitType.trim(),
      };
    }
    if (heading === "Casing") {
      casing = {
        length: casingLength.trim(),
        type: casingType.trim(),
        size: casingSize.trim(),
      };
    }
    const buildToVal = buildTo === "" ? undefined : +buildTo;
    if (editing) {
      onEdit?.(heading, computedName, Number(quantity) || 0, subs, bits, casing, buildToVal, uom);
    } else {
      onAdd(heading, computedName, Number(quantity) || 0, subs, bits, casing, buildToVal, uom);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit item" : "Add item"}</DialogTitle>
          <DialogDescription>
            {editing
              ? "Update the item details."
              : "Choose a category and enter the item name."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div>
            <Label>Category</Label>
            <MobileSelect
              value={heading}
              onValueChange={setHeading}
              options={OPTIONS}
              placeholder="Select heading"
              disabled={editing}
            />
          </div>
          {heading === "Subs" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Qty</Label>
                <Input
                  type="number"
                  min="0"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>UOM</Label>
                <MobileSelect value={uom} onValueChange={setUom} options={UOM_OPTIONS} placeholder="Select UOM" />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>Item name</Label>
                <Input
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  placeholder="e.g. 12mm rod"
                  className="rounded-xl"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Qty</Label>
                  <Input
                    type="number"
                    min="0"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>UOM</Label>
                  <MobileSelect value={uom} onValueChange={setUom} options={UOM_OPTIONS} placeholder="Select UOM" />
                </div>
              </div>
              <div>
                <Label>Build to</Label>
                <Input
                  type="number"
                  min="0"
                  value={buildTo}
                  onChange={(e) => setBuildTo(e.target.value)}
                  placeholder="Target stock level"
                  className="rounded-xl"
                />
              </div>
            </div>
          )}

          {heading === "Subs" && (
            <div className="space-y-3 rounded-2xl bg-slate-50 p-3 dark:bg-slate-800/40">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Box/Pin</Label>
                  <MobileSelect
                    value={sub1Sel}
                    onValueChange={setSub1Sel}
                    options={SUB_OPTIONS}
                    placeholder="Select"
                  />
                </div>
                <div>
                  <Label>Box/Pin</Label>
                  <MobileSelect
                    value={sub2Sel}
                    onValueChange={setSub2Sel}
                    options={SUB_OPTIONS}
                    placeholder="Select"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Textarea
                  value={sub1Desc}
                  onChange={(e) => setSub1Desc(e.target.value)}
                  placeholder="Description"
                  className="rounded-xl min-h-[64px]"
                />
                <Textarea
                  value={sub2Desc}
                  onChange={(e) => setSub2Desc(e.target.value)}
                  placeholder="Description"
                  className="rounded-xl min-h-[64px]"
                />
              </div>
            </div>
          )}

          {heading === "Bits" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-2xl bg-slate-50 p-3 dark:bg-slate-800/40">
              <div>
                <Label>Thread</Label>
                <Input
                  value={bitThread}
                  onChange={(e) => setBitThread(e.target.value)}
                  placeholder="e.g. API REG"
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Size</Label>
                <Input
                  value={bitSize}
                  onChange={(e) => setBitSize(e.target.value)}
                  placeholder="e.g. 4.25 in"
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Input
                  value={bitType}
                  onChange={(e) => setBitType(e.target.value)}
                  placeholder="e.g. PDC"
                  className="rounded-xl"
                />
              </div>
            </div>
          )}

          {heading === "Casing" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-2xl bg-slate-50 p-3 dark:bg-slate-800/40">
              <div>
                <Label>Length</Label>
                <Input
                  value={casingLength}
                  onChange={(e) => setCasingLength(e.target.value)}
                  placeholder="e.g. 6 m"
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Input
                  value={casingType}
                  onChange={(e) => setCasingType(e.target.value)}
                  placeholder="e.g. Steel"
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Size</Label>
                <Input
                  value={casingSize}
                  onChange={(e) => setCasingSize(e.target.value)}
                  placeholder="e.g. 140 mm"
                  className="rounded-xl"
                />
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-2 pt-1">
            {editing && onDelete ? (
              <Button
                type="button"
                variant="destructive"
                className="rounded-xl gap-2"
                onClick={() => onDelete()}
              >
                <Trash2 className="w-4 h-4" /> Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-xl bg-slate-900 hover:bg-slate-800 gap-2"
              >
                {editing ? (
                  <>
                    <Check className="w-4 h-4" /> Save
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" /> Add
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}